import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";

const TabContext = createContext(null);
export const useTabs = () => useContext(TabContext);

let tabIdCounter = 0;
const nextId = () => `tab-${++tabIdCounter}-${Date.now()}`;

const STORAGE_KEY = "erp_open_tabs";

function labelForPath(path) {
  if (path === "/Projects" || path === "/") return "Projects";
  if (path.includes("purchase-orders3")) return "Purchase Order";
  if (path.includes("work-orders3")) return "Work Order";
  if (path.includes("material-order")) return "Material Order";
  if (path.includes("pieceworker-logs3") || path.includes("piecework-logs"))
    return "Worksheet";
  if (path.includes("change-orders")) return "Change Order";
  if (path === "/overhead") return "Overhead"; // ← add
  if (path.startsWith("/Projects/") || path.startsWith("/projects/"))
    return "Project";
  if (path === "/bills" || path === "/bills3") return "Bills";
  if (path === "/timesheets") return "Timesheets";
  if (path === "/finances") return "Finances";
  if (path === "/payroll-submission") return "Submit Payroll";
  if (path === "/finance-dashboard") return "Finance Dashboard";
  if (path === "/takeoff") return "TakeOff";
  if (path === "/inventory") return "Inventory";
  if (path === "/item-database") return "Database";
  if (path === "/Leads") return "Leads";
  if (path === "/subagreements") return "Subcontractor Agreeements";
  if (path.startsWith("/Leads/")) return "Lead";
  if (path === "/contacts") return "Contacts";
  if (path === "/accounts") return "Accounts";
  if (path === "/pieceworkerlog") return "Worksheets";
  if (path === "/labour-items") return "Labour Items";
  if (path === "/users") return "Settings";
  if (path === "/Prospects") return "Prospects";
  if (path.startsWith("/Prospects/")) return "Prospect";
  if (path === "/Opportunities") return "Opportunities";
  if (path.startsWith("/Opportunities/")) return "Opportunity";
  if (path === "/payroll") return "Payroll";
  return path.split("/").filter(Boolean).slice(-1)[0] || "Tab";
}

// Extracts a /Projects/:id or /projects/:id id from a path, if present.
function projectIdFromPath(path) {
  const m = path.match(/^\/[Pp]rojects\/([^/?]+)/);
  return m ? m[1] : null;
}

// Builds the display label for a tab, preferring a known project name
// (looked up by the project id embedded in the path) over the generic
// path-derived label.
function computeLabel(path, projectNamesById) {
  const pid = projectIdFromPath(path);
  if (pid && projectNamesById[pid]) {
    return projectNamesById[pid];
  }
  return labelForPath(path);
}

function loadPersisted(initialPath) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.tabs?.length) {
        // restore the counter so new IDs don't collide with persisted ones
        const maxNum = parsed.tabs.reduce((m, t) => {
          const n = parseInt((t.id.match(/^tab-(\d+)-/) || [])[1], 10);
          return Number.isFinite(n) ? Math.max(m, n) : m;
        }, 0);
        tabIdCounter = maxNum;
        return {
          tabs: parsed.tabs.map((t) => ({
            ...t,
            focusKey: t.focusKey || 0,
            dirty: false, // never trust persisted dirty state across a reload
          })),
          activeId: parsed.activeId,
          projectNamesById: parsed.projectNamesById || {},
        };
      }
    }
  } catch {
    /* corrupt or unavailable — fall through to default */
  }
  const tab = {
    id: nextId(),
    path: initialPath,
    state: {},
    label: labelForPath(initialPath),
    focusKey: 1,
    dirty: false,
  };
  return { tabs: [tab], activeId: tab.id, projectNamesById: {} };
}

export function TabProvider({ children, initialPath = "/Projects" }) {
  const initial = React.useMemo(
    () => loadPersisted(initialPath),
    [initialPath],
  );
  const [tabs, setTabs] = useState(initial.tabs);
  const [activeId, setActiveId] = useState(initial.activeId);
  const [projectNamesById, setProjectNamesById] = useState(
    initial.projectNamesById,
  );

  // Persist on every change (order, labels, active tab — never dirty flags)
  React.useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          tabs: tabs.map((t) => ({ ...t, dirty: false })),
          activeId,
          projectNamesById,
        }),
      );
    } catch {
      /* quota / unavailable — ignore */
    }
  }, [tabs, activeId, projectNamesById]);

  // Warn on browser tab/window close if any open tab has unsaved work.
  React.useEffect(() => {
    const handler = (e) => {
      const anyDirty = tabs.some((t) => t.dirty);
      if (anyDirty) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [tabs]);

  const openTab = useCallback(
    (path, state = {}, { focusExisting = true } = {}) => {
      setTabs((prev) => {
        if (focusExisting) {
          const existing = prev.find((t) => t.path === path);
          if (existing) {
            setActiveId(existing.id);
            return prev;
          }
        }
        const tab = {
          id: nextId(),
          path,
          state,
          label: labelForPath(path),
          focusKey: 1,
          dirty: false,
        };
        setActiveId(tab.id);
        return [...prev, tab];
      });
    },
    [],
  );

  // Returns a short reason to show in a confirm dialog if the tab is dirty,
  // or null if the tab can be closed/refreshed/navigated away freely.
  const confirmIfDirty = useCallback(
    (id, action = "close") => {
      const tab = tabs.find((t) => t.id === id);
      if (!tab?.dirty) return true;
      const verb =
        action === "close"
          ? "close this tab"
          : action === "refresh"
            ? "refresh this tab"
            : "leave this tab";
      return window.confirm(
        `You have unsaved changes. Are you sure you want to ${verb}? Your work will be lost.`,
      );
    },
    [tabs],
  );

  const closeTab = useCallback(
    (id) => {
      if (!confirmIfDirty(id, "close")) return;
      setTabs((prev) => {
        if (prev.length === 1) return prev; // never close the last tab
        const idx = prev.findIndex((t) => t.id === id);
        const next = prev.filter((t) => t.id !== id);
        setActiveId((curr) => {
          if (curr !== id) return curr;
          const fallback = next[idx] || next[idx - 1] || next[0];
          return fallback.id;
        });
        return next;
      });
    },
    [confirmIfDirty],
  );

  // Closes every tab except one, leaving that one (or a fresh Projects tab
  // if none is specified) as the sole remaining tab.
  const closeAllTabs = useCallback((keepId) => {
    setTabs((prev) => {
      const dirtyOthers = prev.filter((t) => t.id !== keepId && t.dirty);
      if (dirtyOthers.length > 0) {
        const ok = window.confirm(
          `${dirtyOthers.length} tab${dirtyOthers.length > 1 ? "s have" : " has"} unsaved changes. Close all tabs anyway? Unsaved work will be lost.`,
        );
        if (!ok) return prev;
      }
      const keep = prev.find((t) => t.id === keepId);
      if (keep) {
        setActiveId(keep.id);
        return [keep];
      }
      const tab = {
        id: nextId(),
        path: "/Projects",
        state: {},
        label: "Projects",
        focusKey: 1,
        dirty: false,
      };
      setActiveId(tab.id);
      return [tab];
    });
  }, []);

  const focusTab = useCallback((id) => {
    setActiveId(id);
    // Do NOT bump focusKey here — that forces a full remount and wipes
    // in-progress form state. focusKey should only change on an explicit
    // "Refresh" click (see refreshTab) or first mount.
  }, []);

  const refreshTab = useCallback(
    (id) => {
      if (!confirmIfDirty(id, "refresh")) return;
      setTabs((prev) =>
        prev.map((t) =>
          t.id === id
            ? { ...t, focusKey: (t.focusKey || 0) + 1, dirty: false }
            : t,
        ),
      );
    },
    [confirmIfDirty],
  );

  const setTabLabel = useCallback((id, label) => {
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, label } : t)));
  }, []);

  const updateTabLocation = useCallback(
    (id, path, state) => {
      setTabs((prev) =>
        prev.map((t) => {
          if (t.id !== id) return t;
          // Preserve a project-name label across query/hash-only changes,
          // recompute otherwise.
          return {
            ...t,
            path,
            state,
            label: computeLabel(path, projectNamesById),
          };
        }),
      );
    },
    [projectNamesById],
  );

  // Called by ProjectView (or anywhere a project's real name is known) once
  // it's loaded, so tabs pointing at that project id show the real name
  // instead of the generic "Project" label.
  const registerProjectName = useCallback((projectId, name) => {
    if (!projectId || !name) return;
    setProjectNamesById((prev) => {
      if (prev[projectId] === name) return prev;
      return { ...prev, [projectId]: name };
    });
    setTabs((prev) =>
      prev.map((t) =>
        projectIdFromPath(t.path) === projectId ? { ...t, label: name } : t,
      ),
    );
  }, []);

  // Called by a form/screen to mark its owning tab dirty/clean.
  const setTabDirty = useCallback((id, dirty) => {
    setTabs((prev) =>
      prev.map((t) => (t.id === id && t.dirty !== dirty ? { ...t, dirty } : t)),
    );
  }, []);

  // Drag-and-drop reordering. fromId is dropped just before/after toId.
  const reorderTabs = useCallback((fromId, toId) => {
    if (fromId === toId) return;
    setTabs((prev) => {
      const fromIdx = prev.findIndex((t) => t.id === fromId);
      const toIdx = prev.findIndex((t) => t.id === toId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  }, []);

  return (
    <TabContext.Provider
      value={{
        tabs,
        activeId,
        openTab,
        closeTab,
        closeAllTabs,
        focusTab,
        refreshTab,
        setTabLabel,
        updateTabLocation,
        registerProjectName,
        setTabDirty,
        reorderTabs,
        confirmIfDirty,
      }}
    >
      {children}
    </TabContext.Provider>
  );
}
